import React, { useEffect, useState } from "react";
import Loader from "../../../Components/GlobalLoader/Loader";
import CommonModal from "../../../Components/Modals/CommonModal";
import Table from "../../../Components/Tables/Table";
import ExportLayout from "../../../Layout/ExportLayout";
import ImportLayout from "../../../Layout/ImportLayout";
import Tabs from "../../../Shared/Tab/Tab";
import { Tab } from "../../../Shared/Tab/TabProps";
import { usePagination } from "../../../hooks/usePagination";
import { useSorting } from "../../../hooks/useSorting";
import { ApiResponse } from "../../../model/common/apiResponse";
import { useSearchStore } from "../../../store/search/searchStore";
import { ActiveTab, PickListProps } from "../Pick/model/PickList.type";
import { useNavigate } from "react-router-dom";
import { fetchStatusWiseCounts, fetchPickList } from "../Pick/service/pickOverview.service";
import PageHeader from "../../../Layout/PageHeader";
import AssignToUserLayout from "../../../Layout/AssignToUserLayout";

const PickList: React.FC<PickListProps> = ({
  columns,
  db_columns,
  pageName,
  pageTabs,
  search_columns,
  clientId,
  onSelectedRowsChange,
  refreshTrigger
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>({
    order: pageTabs[0]?.order ?? 0,
    name: pageTabs[0]?.name ?? "",
  });
  const { currentPage, handlePageChange, perPage } = usePagination();
  const [showModalForImportAndExport, setShowModalForImportAndExport] =
    useState({
      importModal: false,
      exportModal: false,
    });
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedRow, setSelectedRow] = useState<number>();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [data, setData] = useState<ApiResponse<any> | null>(null);
  const statuses = pageTabs.map((tab) => tab.name);
  const navigation = useNavigate();
  const [isShowCanvas, setShowCanvas] = useState(false);
  const [isAddButtonDisable, setAddButtonDisable] = useState(false);

  const [tabRecordcount, setTabRecordcount] = useState<{
    [key: string]: number;
  }>(
    statuses.reduce((acc, status) => {
      acc[status] = 0;
      return acc;
    }, {} as { [key: string]: number })
  );

  const { sortingData, handleSortChange } = useSorting(handlePageChange);

  const { searchTerm, resetSearch, previousSearchTerm, setPreviousTerm } =
    useSearchStore();

  useEffect(() => {
    if (searchTerm != previousSearchTerm && currentPage != 1) {
      handlePageChange(1);
    }

    let order: any[] = [];
    let conditions: any[] = [["pick_ticket.status", "=", activeTab.name]];
    let filterPayload: any[] = [];
    if (clientId) {
      filterPayload.push(["client.id", "=", clientId]);
    }
    conditions = conditions.concat(filterPayload);
    if (sortingData.column.column_name && sortingData.direction) {
      order = [
        {
          column: sortingData.column.column_name,
          dir: sortingData.direction,
        },
      ];
    }
    const payload: any = {
      columns: db_columns,
      search_columns: search_columns,
      conditions: conditions,
      limit: perPage,
      page: currentPage,
      search: searchTerm,
      order: [
        {
          column: "pick_ticket.pick_number",
          dir: "desc",
        },
      ],
    };
    if (order.length > 0) {
      Object.assign(payload, { order });
    }

    setData(null);
    fetchStatusWiseCounts(statuses as string[], searchTerm, filterPayload, search_columns)
      .then((res: ApiResponse<any>) => {
        if (res?.data) {
          setTabRecordcount((prevState) => {
            const updatedCounts: { [key: string]: number } = {
              ...prevState,
              [activeTab.name]: res.data[activeTab.name] || 0,
            };

            statuses.forEach((status) => {
              updatedCounts[status] = res.data[status] || 0;
            });
            return updatedCounts;
          });
        }
      })
      .then(() => {
        fetchPickList(currentPage, payload).then((response: ApiResponse<any>) => {
          setData(response ?? null);
          setPreviousTerm(searchTerm);
        });
      })
      .catch((error: Error) => {
        console.error("Error fetching status-wise counts:", error);
      });
  }, [currentPage, activeTab, searchTerm, sortingData, clientId, refreshTrigger]);

  const handleSelectedRowsChange = (newSelectedRows: Set<number>) => {
    const row_no = Array.from(newSelectedRows)[0];
    setSelectedRow(data?.data?.results[row_no]?.id);
    setSelectedRows(newSelectedRows);
    setAddButtonDisable(newSelectedRows.size === 0);
    onSelectedRowsChange?.(newSelectedRows);
  };

  const onTabClick = (tab: Tab) => {
    setActiveTab({ order: tab.order ?? 0, name: tab.name ?? "new" });
    handlePageChange(1);
  };

  const handleGlobalFilter = () => {
    setShowCanvas(!isShowCanvas);
  };

  const handleGlobalSearch = () => {};

  const handleAssignAction = () => {
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
  };

  const handleShow = () => {
    setShowModalForImportAndExport((prevState: any) => ({
      ...prevState,
      importModal: false,
      exportModal: false,
    }));
  };

  const renderTable = (multiSelect?: boolean | null) => {
    return (
      <Table
        customClassName="sph-table-color"
        enableSorting={true}
        columns={columns}
        data={data?.data?.results || []}
        pagination={{
          recordsPerPage: perPage,
          totalRecords: data?.data?.recordsTotal,
          currentPage: currentPage,
          onCurrentPageChange: handlePageChange,
        }}
        multiselect={false}
        onSortChange={handleSortChange}
        sortingData={sortingData}
        onSelectedRowsChange={handleSelectedRowsChange}
        CheckboxStatus={true}
        checkboxDisableCondition={() => false}
      />
    );
  };

  const tabs = pageTabs.map((tab) => ({
    ...tab,
    label: `${tab.tabLable} | ${tabRecordcount[tab.name] ?? 0}`,
    content: renderTable(tab.multiSelect),
  }));

  return data ? (
    <>
      <PageHeader
        name="Assign"
        isModal={true}
        heading="Assign To"
        nameButtonStatus={!isAddButtonDisable}
        handleGlobalFilter={handleGlobalFilter}
        handleGlobalSearch={handleGlobalSearch}
        content={<AssignToUserLayout handleClose={handleCloseAssignModal} />}
        show={showAssignModal}
        setShow={setShowAssignModal}
      />
      <h5 className="text-start mt-1 mb-3">{pageName}</h5>
      <div className="inbound-CheckIn-Table-Main-Div"></div>

      <div>
        <Tabs
          tabs={tabs}
          isCheckbox={true}
          onTabClick={onTabClick}
          active={activeTab.order}
        />
      </div>
      <div className="inbound-CheckIn-Table-Main-Div"></div>

      <CommonModal
        show={showModalForImportAndExport.importModal}
        handleClose={() =>
          setShowModalForImportAndExport((preValue: any) => {
            return { ...preValue, importModal: false };
          })
        }
        heading={"Import File"}
        content={<ImportLayout handleClose={handleShow} />}
        modalSize="modal-lg"
        footerButtonsActive={false}
        customClassNameForBody=""
        customClassNameForHeader="create-kit-orders"
      />
      <CommonModal
        show={showModalForImportAndExport.exportModal}
        handleClose={() =>
          setShowModalForImportAndExport((preValue: any) => {
            return { ...preValue, exportModal: false };
          })
        }
        heading={"Export"}
        content={<ExportLayout handleClose={handleShow} />}
        modalSize="modal-lg"
        footerButtonsActive={false}
        customClassNameForBody=""
        customClassNameForHeader="create-kit-orders"
      />
    </>
  ) : (
    <Loader />
  );
};

export default PickList;
